import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { appConfigStore } from 'Models/AppConfigStore';
import { BookingEntity } from 'Models/Entities';
import { CustomBookingStatus } from 'Models/Entities/BookingEntity';
import { store } from 'Models/Store';
import { getMessageByKey } from 'Stores/LanguageStore';
import { markBookingAsInvoiced, undoBookingInvoiced } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { undoCancelBooking } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { CancelEventBookingModal } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CancelEventBookingModal';
import { useCancelBooking } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/useCancelBooking';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { Alignment, ButtonGroup } from 'Views/Components/Button/ButtonGroup';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { resendConfirmationEmailModal } from '../../ResendConfirmation/ResendConfirmationEmailModal';
import { bookingIsInCancellableState, bookingRuleFacts } from './Helpers/CanEditBooking';
import { onTransfer } from './Helpers/OnTransfer';
import { invoicedConfirmModal } from './Modals/InvoicedConfirmationModal';
import { transferBookingModal } from './Modals/TransferBookingModal';
import { EditBookingWizard } from './Modals/EditBooking/EditBookingWizard';
import { EditOption } from './Modals/EditBooking/GetBookingEditOptions';
import { EditType } from './BookingDetailedView';

export interface BookingActionButtonsProps {
	booking: BookingEntity;
	canModify: boolean;
	canEdit: boolean;
	editOptions: EditOption[];
	isTwoWay: boolean;
	onSelectEdit: (editType: EditType, alterReturn: boolean) => void;
	customStatus?: CustomBookingStatus;
	refresh: () => void;
	isEventBooking: boolean;
}

function BookingActionButtons({
	booking,
	canModify,
	canEdit,
	editOptions,
	isTwoWay,
	onSelectEdit,
	customStatus,
	refresh,
	isEventBooking,
}: BookingActionButtonsProps) {
	const navigate = useNavigate();
	const [eventCancelOpen, setEventCancelOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [manageOpen, setManageOpen] = useState(false);
	const linkedLeg = booking.returnBooking ?? booking.returnBookingFor;
	const {
		trigger: openCancellationWizard,
		loading,
		modal: cancellationModal,
	} = useCancelBooking({
		bookingId: booking.id,
		userFirstName: booking.user.firstName,
		userLastName: booking.user.lastName,
		bookingLegacyId: booking.legacyId,
		notices: {
			departureDateTime: booking.bookedSummary?.ferryTrip?.departureDateTime,
			checkedIn: !!(booking.checkedIn || booking.checkInStatusId),
			linkedLegCheckedIn: !!linkedLeg && !!(linkedLeg.checkedIn || linkedLeg.checkInStatusId),
		},
		refresh,
	});

	const canCancel = canModify && canCancelBooking(booking);

	// Freight bookings produce no edit options (their edit nav isn't wired up), so the button
	// hides automatically for them.
	const showEditButton = canEdit && booking.bookingStatus !== 'CANCELLED' && editOptions.length > 0;

	const handleCancelClick = async () => {
		// Don't use the native `disabled` attribute for the busy state — global
		// .btn[disabled] styling strips the red text colour and removes the row's
		// bottom border on hover, making the button visually disappear from the
		// action panel. Guarding here keeps the same look while the rules fetch runs.
		if (loading) return;
		if (!isEventBooking) {
			await openCancellationWizard();
		} else {
			// For event bookings, go directly to cancel
			onCancelEventBooking(booking, () => setEventCancelOpen(true));
		}
	};

	const handleRestoreBookingClick = async () => {
		try {
			newConfirmModal(
				'Restore booking?',
				'Are you sure you want to restore this booking (and the associated booking if it was also cancelled at the same time)?',
				{
					confirmText: 'Restore',
					cancelText: 'Back',
				},
			)
				.then(async () => {
					await undoCancelBooking(booking.id);
					refresh();
					alertToast('Booking has been successfully restored.', 'success');
				})
				.catch(() => {}); // Do nothing
		} catch (error) {
			alertToast('Could not un-do the cancelled booking.', 'error');
		}
	};

	const changeInvoiceStatus = (setToInvoiced: boolean) => {
		invoicedConfirmModal(setToInvoiced)
			.then(async () => {
				if (setToInvoiced) {
					markBookingAsInvoiced(booking.id).then(
						action(x => {
							booking.isInvoiced = setToInvoiced;
							refresh();
						}),
					);
				} else {
					undoBookingInvoiced(booking.id).then(
						action(x => {
							booking.isInvoiced = setToInvoiced;
							refresh();
						}),
					);
				}
			})
			.catch(() => {
				// do nothing because we are just closing the modal
			});
	};

	const resendConfirmationEmail = () => {
		const bookingIds: string[] = [booking.id];
		if (booking.returnBooking?.id) {
			bookingIds.push(booking.returnBooking.id);
		}
		if (booking.returnBookingForId) {
			bookingIds.push(booking.returnBookingForId);
		}

		resendConfirmationEmailModal(Array.from(new Set(bookingIds)), booking.user.email);
	};

	const handleTransferBookingClick = async () => {
		const { proceed, transferBoth } = await onTransfer(booking);
		if (!proceed) {
			return;
		}
		transferBookingModal(booking, transferBoth, () => {
			refresh();
			navigate(`/bookings/${booking.id}`);
		});
	};

	const transferFerryTrip = booking.bookedSummary?.ferryTrip;
	const transferBookingCategory = booking.typeOfBooking();
	const isTransferCategoryClosed =
		(transferBookingCategory === 'VEHICLE' && transferFerryTrip?.vehicleBookingsClosed) ||
		(transferBookingCategory === 'FREIGHT' && transferFerryTrip?.freightBookingsClosed) ||
		(transferBookingCategory === 'PASSENGER_ONLY' && transferFerryTrip?.passengerBookingsClosed);

	// Uses the trip-wide departure time as a pragmatic pre-hide; on multi-stop trips this
	// can disagree with the booking's actual start-stop departure, but the backend safety
	// net rejects past-trip transfers and the in-modal error surfaces the reason.
	const transferDepartureDateTime = booking.bookedSummary?.ferryTrip?.departureDateTime;
	const hasTransferDeparted = !!transferDepartureDateTime && isBeforeNow(transferDepartureDateTime);

	const canTransferBooking =
		appConfigStore.bookingTransferEnabled &&
		// The capability /transfer-booking itself demands. The config flag stays beside it: one says
		// whether this operator transfers bookings at all, the other says who may.
		store.can('TransferBooking') &&
		booking.bookingStatus !== 'CANCELLED' &&
		booking.transactionStatus !== 'INVOICED' &&
		booking.transactionStatus !== 'AWAITING' &&
		!booking.checkedIn &&
		!isEventBooking &&
		!hasTransferDeparted &&
		!isTransferCategoryClosed;

	// Run an action from the manage-booking modal: close the modal first so the action's own
	// modal/navigation takes over cleanly.
	const runManageAction = (callback: () => void) => {
		setManageOpen(false);
		callback();
	};

	const isStaffInvoiceActor = store.isAdmin || store.isManager;

	// Use the full status list so a cancelled booking that also carries an invoice state
	// (CANCELLED + AWAITING / INVOICED) still surfaces the set/undo-invoiced actions. Falls back to
	// the single status when the list isn't provided.
	const invoiceStatuses = booking.transactionStatuses?.length ? booking.transactionStatuses : [customStatus];

	const manageActions: ManageBookingAction[] = [];

	if (showEditButton) {
		manageActions.push({
			id: 'edit',
			title: getMessageByKey('booking-details__edit-booking-button__label', 'Edit this booking'),
			// A site that does not sell vehicles has no vehicle on any booking, so naming one here
			// offered a change the wizard will not then present.
			description: appConfigStore.vehicleEnabled
				? getMessageByKey(
						'booking-details__edit-booking-button__description',
						'Change the trip, passengers, vehicle and more.',
					)
				: getMessageByKey(
						'booking-details__edit-booking-button__description-no-vehicle',
						'Change the trip, passengers and more.',
					),
			icon: 'edit',
			iconColor: 'bulk-action-card__icon--blue',
			onClick: () => runManageAction(() => setEditOpen(true)),
		});
	}
	if (isStaffInvoiceActor && invoiceStatuses.includes('AWAITING')) {
		manageActions.push({
			id: 'invoice',
			title: getMessageByKey('booking-details__invoice-button__label', 'Mark as invoiced'),
			description: 'Mark this booking as invoiced.',
			icon: 'description',
			iconColor: 'bulk-action-card__icon--blue',
			onClick: () => runManageAction(() => changeInvoiceStatus(true)),
		});
	}
	if (isStaffInvoiceActor && invoiceStatuses.includes('INVOICED')) {
		manageActions.push({
			id: 'undo-invoice',
			title: getMessageByKey('booking-details__undo-invoice-button__label', 'Mark as not invoiced'),
			description: 'Mark this booking as not invoiced.',
			icon: 'description',
			iconColor: 'bulk-action-card__icon--blue',
			onClick: () => runManageAction(() => changeInvoiceStatus(false)),
		});
	}
	if (booking.bookingStatus !== 'CANCELLED' && !isEventBooking) {
		manageActions.push({
			id: 'resend',
			title: 'Share this ticket',
			description: 'Send the ticket to another email address.',
			icon: 'mail',
			iconColor: 'bulk-action-card__icon--blue',
			onClick: () => runManageAction(resendConfirmationEmail),
		});
	}
	if (canTransferBooking) {
		manageActions.push({
			id: 'transfer',
			title: getMessageByKey('booking-details__transfer-booking-button__label', 'Transfer this booking'),
			description: 'Transfer the booking to another account.',
			icon: 'swap_horiz',
			iconColor: 'bulk-action-card__icon--orange',
			onClick: () => runManageAction(handleTransferBookingClick),
		});
	}
	if (canCancel) {
		manageActions.push({
			id: 'cancel',
			title: getMessageByKey('booking-details__cancel-booking-button__label', 'Cancel this booking'),
			description: 'Cancel your trip.',
			icon: 'delete',
			iconColor: 'bulk-action-card__icon--red',
			onClick: () => runManageAction(handleCancelClick),
		});
	}
	if (canRestoreBooking(booking)) {
		manageActions.push({
			id: 'restore',
			title: getMessageByKey('booking-details__restore-booking-button__label', 'Restore this booking'),
			description: 'Restore this cancelled booking.',
			icon: 'undo',
			iconColor: 'bulk-action-card__icon--blue',
			onClick: () => runManageAction(handleRestoreBookingClick),
		});
	}

	return (
		<>
			{manageActions.length > 0 && (
				// Single entry point styled as a regular action row (matching the legacy action
				// buttons) that opens the full manage-booking modal.
				<ButtonGroup className="booking-actions__button-group" alignment={Alignment.VERTICAL}>
					<Button
						className="manage-booking-button"
						type="button"
						sizes={Sizes.Medium}
						colors={Colors.Secondary}
						display={Display.Text}
						onClick={() => setManageOpen(true)}
						buttonProps={
							{
								'data-test-id': 'manage-booking-button',
								'aria-busy': loading || undefined,
							} as any
						}
					>
						<Icon name="settings" useMaterialIcons />
						{loading ? 'Preparing…' : 'Manage booking'}
						<Icon name="chevron_right" useMaterialIcons />
					</Button>
				</ButtonGroup>
			)}
			{/* The manage modal closes as soon as "Cancel this booking" is picked, and the rules fetch
			    behind it can take a few seconds - long enough that the only feedback, the "Preparing…"
			    label back on the action row, goes unnoticed. Hold a spinner over the page until the
			    cancellation wizard is ready to open. */}
			{loading && (
				<NewModal isOpen label="Preparing cancellation" modalSize="sm" className="preparing-cancellation-modal">
					<NewModal.Body>
						{/* `relative small` because the spinner's default styling is
						    `position: absolute; height: 100%; width: 100%` - it fills the modal body and
						    sits on top of the text underneath it. */}
						<div className="flex flex-col items-center justify-center gap-3 p-8">
							<LottieSpinner className="relative small" />
							<p className="m-0 text-center">Preparing cancellation…</p>
						</div>
					</NewModal.Body>
				</NewModal>
			)}
			{manageOpen && (
				<NewModal
					isOpen
					label="Manage booking"
					className="bulk-actions-modal manage-booking-modal"
					onRequestClose={() => setManageOpen(false)}
				>
					<NewModal.Header>
						<div>
							<NewModal.Title>Manage booking</NewModal.Title>
							<p className="bulk-actions-modal__subtitle">#{booking.humanReadableId}</p>
						</div>
						<button
							type="button"
							className="bulk-actions-modal__close-btn"
							onClick={() => setManageOpen(false)}
						>
							<Icon name="cross" />
						</button>
					</NewModal.Header>
					<NewModal.Body>
						<div className="bulk-action-cards">
							{manageActions.map(manageAction => (
								<button
									key={manageAction.id}
									type="button"
									className="bulk-action-card"
									data-test-id={`manage-action-${manageAction.id}`}
									onClick={manageAction.onClick}
								>
									<div className={`bulk-action-card__icon ${manageAction.iconColor}`}>
										<Icon name={manageAction.icon} useMaterialIcons />
									</div>
									<div className="bulk-action-card__content">
										<p className="bulk-action-card__title">{manageAction.title}</p>
										<p className="bulk-action-card__description">{manageAction.description}</p>
									</div>
									<span className="bulk-action-card__chevron">
										<Icon name="chevron-right" />
									</span>
								</button>
							))}
						</div>
					</NewModal.Body>
				</NewModal>
			)}
			{editOpen && (
				<EditBookingWizard
					booking={booking}
					isTwoWay={isTwoWay}
					editOptions={editOptions}
					onClose={() => setEditOpen(false)}
					onSelectEdit={onSelectEdit}
				/>
			)}
			{cancellationModal}
			{eventCancelOpen && (
				<CancelEventBookingModal
					booking={booking}
					refresh={refresh}
					navigate={navigate}
					onClose={() => setEventCancelOpen(false)}
				/>
			)}
		</>
	);
}

interface ManageBookingAction {
	id: string;
	title: string;
	description: string;
	icon: string;
	/** One of the bulk-action-card icon colour modifier classes (blue/orange/red). */
	iconColor: string;
	onClick: () => void;
}

export default observer(BookingActionButtons);

export const onCancelEventBooking = (_booking: BookingEntity, openWizard: () => void) => {
	if (store.isStaff) {
		openWizard();
	} else {
		newAlertModal(
			'Cancel event booking',
			<div>
				<h5>Please contact staff to cancel this booking.</h5>
			</div>,
			{
				cancelText: 'Okay',
			},
		).finally(() => {
			if (!!(window as any).Intercom) {
				(window as any).Intercom('show');
			}
		});
	}
};

export const canCancelBooking = (bookingToCancel: BookingEntity, mainBooking?: BookingEntity) => {
	if (!bookingToCancel.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return;
	}

	return (
		bookingIsInCancellableState(bookingRuleFacts(bookingToCancel)) &&
		(isNullOrUndefined(mainBooking?.checkedIn) || !mainBooking?.checkedIn) &&
		isNullOrUndefined(mainBooking?.checkInStatusId)
	);
};

export const canRestoreBooking = (bookingToCancel: BookingEntity, mainBooking?: BookingEntity) => {
	if (!bookingToCancel.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return;
	}

	// Not tested yet... need to test before it can be used
	return false;

	// return isNotNullOrUndefined(bookingToCancel)
	// 	&& (!isBeforeNow(bookingToCancel.bookedSummary?.ferryTrip.departureDateTime) || store.isSuper)
	// 	&& bookingToCancel.bookingStatus === 'CANCELLED'
	// 	&& store.isSuper;
};
